import { BaseAction } from "../../../core/actions/BaseAction";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { isEmpty, isInbound } from "../../../Lib/Utils/validationUtil";
import { sortAddonsPassengersByType } from "../../../Lib/Utils/addons";
import { isLiveChangeDateCheckout } from "../../../Lib/Utils/changeDate";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IAddonsPassengerLeg, IAlreadyAddonsForPassenger, IConfirmAddonsActionParams } from "../../../Orchestrator/Types/Interface/IAddOns";
import { IPaymentItemDetails } from "../../../Orchestrator/Types/Interface/IPayment";
import { confirmRemovedAddonsAction } from "./ConfirmRefundedAddonsAction";

export class ConfirmAddonsAction extends BaseAction<
  IDefaultActionResult,
  IConfirmAddonsActionParams
> {
  override async run(): Promise<IDefaultActionResult | null> {
    const actionResult: IDefaultActionResult = {
      data: null,
      message: "",
      status: "fail",
    };
    // `manageSeatingBaselineAddonsLegPassengerAtom` is populated earlier in the
    // manage-seating flow and only reset by `ResetManageSeatingStateAction`.
    // Use it alongside the live `isManageSeatingAtom` as a belt-and-suspenders
    // signal: route to the manage-seating branch whenever either says we're in
    // manage-seating — ensures preselected FULFILLED items don't get billed
    // again through the non-manage path.
    const isManageSeatingAtomLive = getRecoilStateValue("isManageSeatingAtom")
    const baseline = getRecoilStateValue("manageSeatingBaselineAddonsLegPassengerAtom") as object | null | undefined
    const isManageSeating = isManageSeatingAtomLive || !isEmpty(baseline)
    const isUpgrade = getRecoilStateValue("isUpgradeToPremiumStateAtom") as boolean;

    // Helper: merge incoming legs into the existing confirmAddonsPassengerListAtom
    // so that confirming leg A does not wipe leg B's confirmed state out of the atom.
    const mergeIntoConfirmedList = (incoming: IAddonsPassengerLeg[]) => {
      const existing = (getRecoilStateValue("confirmAddonsPassengerListAtom") as IAddonsPassengerLeg[]) || [];
      const incomingIds = new Set((incoming || []).map((l) => l.legId));
      const merged = [
        ...existing.filter((l) => !incomingIds.has(l.legId)),
        ...(incoming || []),
      ];
      updateRecoilState("confirmAddonsPassengerListAtom", merged);
    };

    if (isManageSeating) {
      try {
        let { addonsPassengerList = [], paymentSummary, bookedTrips, isFromPrepare = "" } = this.params!
        mergeIntoConfirmedList(addonsPassengerList);

        paymentSummary = {
          ...paymentSummary,
          otherDetails: {
            ...paymentSummary?.otherDetails || {},
            addons: {}
          }
        }

        const alreadyAddonsLegPassenger: any = getRecoilStateValue("alreadyAddonsLegPassengerAtom")
        let allWayAddons: IAlreadyAddonsForPassenger[] = []
        if (alreadyAddonsLegPassenger) {
          allWayAddons = [
            ...(alreadyAddonsLegPassenger["outbound"] || []),
            ...(alreadyAddonsLegPassenger["inbound"] || []),
          ];
        }

        /**
         * Filter all the new check addons means new addons
        */
        const newConfirmedAddons: IAddonsPassengerLeg[] = (addonsPassengerList || [])
          .map(({ ...leg }) => {
            const addonsPassenger = leg.addonsPassenger.map(({ addonItem, ...rest }) => {
              const filteredItems = Object.fromEntries(
                Object.entries(addonItem).filter(([_, v]) => v?.isNew === true)
              );
              return {
                ...rest,
                addonItem: filteredItems
              };
            }).filter(p => Object.keys(p.addonItem).length > 0); // remove passengers with no isNew addons

            return { ...leg, addonsPassenger };
          })
          .filter(leg => leg.addonsPassenger.length > 0);

        updateRecoilState("manageAddonsNewConfirmedAddonsAtom", newConfirmedAddons)

        /**
         * Calculate all the uncheck addons means refund addons
        */
        confirmRemovedAddonsAction.setParams({ addonsPassengerList: addonsPassengerList })
        await confirmRemovedAddonsAction.execute();
        const removedAddons = confirmRemovedAddonsAction?.result?.data || []
        // const keptSet = new Set();
        // (addonsPassengerList || []).forEach(leg => {
        //   leg.addonsPassenger.forEach(passenger => {
        //     Object.values(passenger?.addonItem || {}).forEach((item: any) => {
        //       keptSet.add(`${leg.legId}-${passenger.passengerId}-${item.label.replaceAll(" ", "_").toLowerCase()}`);
        //     });
        //   });
        // });
        // const removedAddons: IAlreadyAddonsForPassenger[] = (allWayAddons || []).filter((item: any) => {
        //   const key = `${item.legId}-${item.passengerId}-${item.addonName.replaceAll(" ", "_").toLowerCase()}`;
        //   return !keptSet.has(key);
        // });

        // updateRecoilState("manageAddonsRemovedAddonsAtom", removedAddons)

        const totalNewAddonsPrice = newConfirmedAddons.reduce((legAcc, leg) => {
          return (
            legAcc +
            leg.addonsPassenger.reduce((passAcc, passenger) => {
              return (
                passAcc +
                Object.values(passenger.addonItem || {}).reduce((addonAcc: number, addon: any) => {
                  return addonAcc + (addon.price || 0); // assume addon has price field
                }, 0)
              );
            }, 0)
          );
        }, 0);

        const totalRemovedAddonsPrice = removedAddons.reduce((acc: number, addon: any) => {
          return acc + (addon.price || 0); // assume addon has price field
        }, 0);

        const finaleAmount = totalNewAddonsPrice - totalRemovedAddonsPrice
        const alreadyPaidMs = paymentSummary?.payment?.alreadyPaid || 0;
        if (finaleAmount < 0) {
          paymentSummary = {
            ...paymentSummary,
            payment: {
              ...paymentSummary?.payment || {},
              refundedAmount: Math.abs(finaleAmount),
              total: 0,
              subTotal: alreadyPaidMs,
            },
            otherDetails: {
              ...paymentSummary?.otherDetails || {},
              isRefund: true
            }
          }
        } else {
          paymentSummary = {
            ...paymentSummary,
            payment: {
              ...paymentSummary?.payment || {},
              refundedAmount: 0,
              total: finaleAmount,
              subTotal: finaleAmount + alreadyPaidMs,
            },
            otherDetails: {
              ...paymentSummary?.otherDetails || {},
              isRefund: false
            }
          }
        }

        /**
         * Added newly selected addons into payment summary
        */
        const directionLeg: Record<string, IPaymentItemDetails[]> = {};
        newConfirmedAddons.forEach((passenger) => {
          const matchedTrip = bookedTrips.find(d => d.legs.some(d => d.id === passenger.legId));
          if (!matchedTrip) {
            console.warn("[ConfirmAddonsAction] No matching trip for legId", passenger.legId);
            return;
          }
          const direction = isInbound(matchedTrip.direction) ? TrainTypeEnum.inbound : TrainTypeEnum.outbound
          const transformedMealOptions: IPaymentItemDetails[] = []
          const allLegPass = sortAddonsPassengersByType(passenger?.addonsPassenger || []);
          const addonCounts: Record<string, { count: number; price: number }> = {};

          allLegPass.forEach(({ addonItem, passengerId }) => {
            Object.entries(addonItem).forEach(([key, item]) => {
              if (item && typeof item === "object" && "label" in item && "price" in item) {
                const alreadyCategoryAddons = allWayAddons.find(d => d.legId === item.coveredLegId && d.category === key && d.passengerId === passengerId)
                const label = item.label;
                let price = item.price;
                // if (alreadyCategoryAddons) {
                //   const oldPrice = alreadyCategoryAddons?.price || 0
                //   if (oldPrice > 0 && price >= oldPrice) {
                //     price = price - oldPrice
                //   } else {
                //     price = 0
                //   }
                // }
                if (!addonCounts[label]) {
                  addonCounts[label] = { count: 0, price: 0 };
                }
                addonCounts[label].count += 1;
                addonCounts[label].price += price;
              }
            });
          });

          Object.entries(addonCounts).forEach(([label, { count, price }]) => {
            const transformedMeal: IPaymentItemDetails = {
              amount: price,
              description: `${count} ${label}`,
              category: label,
              count: count
            };
            transformedMealOptions.push(transformedMeal);
          });

          directionLeg[direction] = [
            ...directionLeg[direction] || [],
            ...transformedMealOptions
          ]
        })

        Object.keys(directionLeg).forEach((direction) => {
          if (directionLeg[direction].length > 0) {
            const updateDirectionLegForPaymentSummary = directionLeg[direction].reduce((acc: Record<string, { amount: number, count: number }>, item: IPaymentItemDetails) => {
              const categoryText = item?.category || ""
              if (!acc[categoryText]) {
                acc[categoryText] = { amount: 0, count: 0 };
              }
              acc[categoryText].amount += item.amount;
              acc[categoryText].count += item?.count || 0;
              return acc;
            }, {});

            let directionLegDetail = Object.entries(updateDirectionLegForPaymentSummary).map(([description, { amount, count }]) => ({
              amount,
              description: `${count} ${description.replace(/^\d+\s/, '')}`
            }))

            paymentSummary = {
              ...paymentSummary,
              otherDetails: {
                ...paymentSummary?.otherDetails || {},
                addons: {
                  ...paymentSummary?.otherDetails?.addons || {},
                  [direction]: directionLegDetail
                }
              }
            }
          }
        })

        /**
         * Added removed addons into payment summary
        */
        const cancelDirectionLeg: Record<string, IPaymentItemDetails[]> = {};
        const removedAddonsStructured: IAddonsPassengerLeg[] = Object.values(
          (removedAddons || []).reduce((acc: any, addon: any) => {
            const { legId, passengerId, type, ...rest } = addon;

            if (!acc[legId]) {
              acc[legId] = { legId, addonsPassenger: [] };
            }

            let passenger = acc[legId].addonsPassenger.find((p: any) => p.passengerId === passengerId);
            if (!passenger) {
              passenger = { passengerId, addonItem: {} };
              acc[legId].addonsPassenger.push(passenger);
            }

            // Put addon back inside addonItem with type as key
            passenger.addonItem[type] = { ...rest };

            return acc;
          }, {}))
        removedAddonsStructured.forEach((passenger) => {
          const matchedTrip = bookedTrips.find(d => d.legs.some(d => d.id === passenger.legId));
          if (!matchedTrip) {
            console.warn("[ConfirmAddonsAction] No matching trip for removed legId", passenger.legId);
            return;
          }
          const direction = isInbound(matchedTrip.direction) ? TrainTypeEnum.inbound : TrainTypeEnum.outbound
          const transformedMealOptions: IPaymentItemDetails[] = []
          const allLegPass = passenger?.addonsPassenger || [];
          const addonCounts: Record<string, { count: number; price: number }> = {};

          allLegPass.forEach(({ addonItem, passengerId }) => {
            Object.entries(addonItem).forEach(([key, item]) => {
              if (item && typeof item === "object" && "addonName" in item && "price" in item) {
                const label = item.addonName as string
                let price = item.price;
                if (!addonCounts[label]) {
                  addonCounts[label] = { count: 0, price: 0 };
                }
                addonCounts[label].count += 1;
                addonCounts[label].price += price;
              }
            });
          });

          Object.entries(addonCounts).forEach(([label, { count, price }]) => {
            const transformedMeal: IPaymentItemDetails = {
              amount: price,
              description: `${count} ${label}`,
              category: label,
              count: count
            };
            transformedMealOptions.push(transformedMeal);
          });

          cancelDirectionLeg[direction] = [
            ...cancelDirectionLeg[direction] || [],
            ...transformedMealOptions
          ]
        })

        Object.keys(cancelDirectionLeg).forEach((direction) => {
          if (cancelDirectionLeg[direction].length > 0) {
            const updateDirectionLegForPaymentSummary = cancelDirectionLeg[direction].reduce((acc: Record<string, { amount: number, count: number }>, item: IPaymentItemDetails) => {
              const categoryText = item?.category || ""
              if (!acc[categoryText]) {
                acc[categoryText] = { amount: 0, count: 0 };
              }
              acc[categoryText].amount += item.amount;
              acc[categoryText].count += item?.count || 0;
              return acc;
            }, {});

            let directionLegDetail = Object.entries(updateDirectionLegForPaymentSummary).map(([description, { amount, count }]) => ({
              amount,
              description: `${count} ${description.replace(/^\d+\s/, '')} (Canceled)`
            }))

            paymentSummary = {
              ...paymentSummary,
              otherDetails: {
                ...paymentSummary?.otherDetails || {},
                addons: {
                  ...paymentSummary?.otherDetails?.addons || {},
                  [direction]: [...(((paymentSummary?.otherDetails?.addons || {})[direction]) || []), ...directionLegDetail]
                }
              }
            }
          }
        })

        // Auto-fired prepare call (PreparePassengerForAddons → here on modal-
        // open / page-mount) must NOT overwrite the manage-seating payment
        // summary. PrepareBookingSummary already produced the correct buckets
        // (active / pendingRefund / canceled) from the booking + the refund
        // queue. This action's own rebuild only re-derives `newConfirmedAddons`
        // (`isNew === true` items) and `removedAddons` (diff against the
        // already-addons baseline) — neither of which knows about the
        // pendingRefund queue. The result for cancel-back is a stale (Canceled)
        // row replacing the (Refund) row PBS just wrote.
        //
        // Modal-confirm (AddOnsInitStep) doesn't pass isFromPrepare, so its
        // write still proceeds as before. The gate is also implicitly mode-
        // scoped: we're inside `if (isManageSeating)` (line 44), so it can
        // never affect regular checkout / upgrade / change-date paths.
        if (isFromPrepare !== "prepareAddons") {
          updateRecoilState("checkoutPaymentSummaryAtom", paymentSummary)
        }
        actionResult.status = "success";
      } catch (error: any) {
        console.error("[ConfirmAddonsAction] Failed to confirm addons:", error);
        actionResult.message = error.message;
      } finally {
        return actionResult;
      }
    } else {
      try {
        let { addonsPassengerList = [], paymentSummary, bookedTrips, confirmAddonsPassengerList } = this.params!
        mergeIntoConfirmedList(addonsPassengerList);

        const newConfirmedAddons: IAddonsPassengerLeg[] = (addonsPassengerList || [])
          .map(({ ...leg }) => {
            const addonsPassenger = (leg.addonsPassenger || [])
              .map(({ addonItem, ...rest }) => {
                const filteredItems = Object.fromEntries(
                  Object.entries(addonItem || {}).filter(([_, v]) => v?.isNew === true)
                );
                return { ...rest, addonItem: filteredItems };
              })
              .filter((p: any) => Object.keys(p.addonItem || {}).length > 0);
            return { ...leg, addonsPassenger };
          })
          .filter((leg) => (leg.addonsPassenger || []).length > 0);

        updateRecoilState("manageAddonsNewConfirmedAddonsAtom", newConfirmedAddons);

        // Only subtract prices for legs being re-confirmed in THIS round.
        // Without this filter, confirming inbound would deduct outbound's
        // previously-confirmed addon prices from the running total and wipe
        // outbound's entry from otherDetails.addons (and vice versa).
        const scopeLegIds = new Set(
          (addonsPassengerList || []).map((l) => l?.legId).filter(Boolean) as string[]
        );
        const alreadyConfirmedTotal = (confirmAddonsPassengerList || [])
          .filter((leg) => scopeLegIds.has(leg?.legId || ""))
          .flatMap((leg) => leg?.addonsPassenger || [])
          .flatMap((p) => Object.values(p?.addonItem || {}))
          .reduce((sum, item) => sum + (item?.price || 0), 0);

        // Clear only the directions being confirmed from otherDetails.addons;
        // preserve the other direction's entry. The per-direction loop below
        // will write the fresh entry for each scope direction.
        const scopeDirections = new Set<string>();
        (addonsPassengerList || []).forEach((leg) => {
          const matchedTrip = bookedTrips.find((t) => t.legs.some((l) => l.id === leg.legId));
          if (matchedTrip) {
            scopeDirections.add(
              isInbound(matchedTrip.direction) ? TrainTypeEnum.inbound : TrainTypeEnum.outbound
            );
          }
        });
        const preservedAddons: Record<string, any> = { ...(paymentSummary?.otherDetails?.addons || {}) };
        for (const dir of scopeDirections) {
          delete preservedAddons[dir];
        }

        // Always rebuild otherDetails.addons (preserves the other direction
        // intact). The payment reset is non-CDT-only — in CDT flow,
        // PrepareBookingSummaryAction owns payment.total / subTotal via the
        // per-leg formula, so any reset here would just be an intermediate
        // value that drifts from backend if the refetch+rebuild is skipped.
        paymentSummary = {
          ...paymentSummary,
          otherDetails: {
            ...paymentSummary?.otherDetails || {},
            addons: preservedAddons
          },
        }
        if (!isLiveChangeDateCheckout()) {
          const resetTotal = (paymentSummary?.payment?.total || 0) - alreadyConfirmedTotal;
          paymentSummary = {
            ...paymentSummary,
            payment: {
              ...paymentSummary?.payment || {},
              total: resetTotal,
              subTotal: resetTotal + (paymentSummary?.payment?.alreadyPaid || 0),
            }
          }
        }
        const directionLeg: Record<string, IPaymentItemDetails[]> = {};
        addonsPassengerList.forEach((passenger) => {
          const matchedTrip = bookedTrips.find(d => d.legs.some(d => d.id === passenger.legId));
          if (!matchedTrip) {
            console.warn("[ConfirmAddonsAction] No matching trip for legId", passenger.legId);
            return;
          }
          const direction = isInbound(matchedTrip.direction) ? TrainTypeEnum.inbound : TrainTypeEnum.outbound
          const transformedMealOptions: IPaymentItemDetails[] = []
          const allLegPass = sortAddonsPassengersByType(passenger?.addonsPassenger || []);
          const addonCounts: Record<string, { count: number; price: number }> = {};
          allLegPass.forEach(({ addonItem }) => {
            Object.values(addonItem).forEach((item) => {
              if (item && typeof item === "object" && "label" in item && "price" in item) {
                const label = item.label;
                const price = item.price;
                if (!addonCounts[label]) {
                  addonCounts[label] = { count: 0, price: 0 };
                }
                addonCounts[label].count += 1;
                addonCounts[label].price += price;
              }
            });
          });
          Object.entries(addonCounts).forEach(([label, { count, price }]) => {
            const transformedMeal: IPaymentItemDetails = {
              amount: price,
              description: `${count} ${label}`,
              category: label,
              count: count
            };
            transformedMealOptions.push(transformedMeal);
          });
          directionLeg[direction] = [
            ...directionLeg[direction] || [],
            ...transformedMealOptions
          ]
        })

        let confirmedAddonsPrice = 0
        Object.keys(directionLeg).forEach((direction) => {
          const updateDirectionLegForPaymentSummary = directionLeg[direction].reduce((acc: Record<string, { amount: number, count: number }>, item: IPaymentItemDetails) => {
            const categoryText = item?.category || ""
            if (!acc[categoryText]) {
              acc[categoryText] = { amount: 0, count: 0 };
            }
            acc[categoryText].amount += item.amount;
            acc[categoryText].count += item?.count || 0;
            return acc;
          }, {});

          const directionLegDetail = Object.entries(updateDirectionLegForPaymentSummary).map(([description, { amount, count }]) => ({
            amount,
            description: `${count} ${description.replace(/^\d+\s/, '')}`
          }))

          confirmedAddonsPrice += directionLegDetail.reduce((acc: number, item) => {
            acc += item.amount
            return acc
          }, 0)

          paymentSummary = {
            ...paymentSummary,
            otherDetails: {
              ...paymentSummary?.otherDetails || {},
              addons: {
                ...paymentSummary?.otherDetails?.addons || {},
                [direction]: directionLegDetail
              }
            }
          }
        })

        // Change-date is excluded from this direct payment.total write.
        //
        // Why: ConfirmAddonsAction only knows the *current* confirm-leg's
        // picks and uses a GLOBAL credit (creditOut + creditIn) against a
        // single-leg gross. That can't reproduce the per-leg formula —
        //   max(0, tickets(d) + addons(d) + fees(d) − credit(d))   per d
        // — that backend `provisionalPrice` uses. When credit on the OTHER
        // leg is non-zero, this action's offset over-credits the current leg
        // and the total drifts away from backend (the "Total = 25 instead of
        // 10" bug from the leftover-credit-absorbs-fee scenario).
        //
        // In change-date, `PersistAddonsOnConfirmAction` runs immediately
        // after this action: it DELETE/POSTs the ancillaries, refetches the
        // booking, then calls `prepareBookingSummaryAction` with the
        // per-leg formula — that becomes the authoritative payment.total.
        // We still let `otherDetails.addons` and `confirmAddonsPassengerList`
        // updates above flow through so the receipt's per-leg line items
        // update immediately on Confirm (before the refetch returns).
        //
        // Other flows (regular checkout, quick-exchange, book-it-again) keep
        // the original "layer gross addon cost on top of current total"
        // behavior. They have no per-leg credit concept, so the global-sum
        // math here was already correct for them — the prior carryOver/credit
        // branch was dead code outside change-date and is removed.
        if (!isLiveChangeDateCheckout()) {
          const finalTotal = (paymentSummary?.payment?.total || 0) + confirmedAddonsPrice;
          paymentSummary = {
            ...paymentSummary,
            payment: {
              ...paymentSummary?.payment || {},
              total: finalTotal,
              subTotal: finalTotal + (paymentSummary?.payment?.alreadyPaid || 0),
            }
          }
        }
        updateRecoilState("checkoutPaymentSummaryAtom", paymentSummary)

        actionResult.status = "success";
      } catch (error: any) {
        console.error("[ConfirmAddonsAction] Failed to confirm addons:", error);
        actionResult.message = error.message;
      } finally {
        return actionResult;
      }
    }
  }
}

export const confirmAddonsAction = new ConfirmAddonsAction();
